import { FC, memo } from 'react'
import { useNavigate } from 'react-router-dom'
import { FixedSizeList, ListChildComponentProps } from 'react-window'
import InfiniteLoader from 'react-window-infinite-loader'
import { EntityPostVisibility } from '@ryddm-inc/ryddm-apiclient'

// config
import { Path } from '@/config'
// hooks
import { useAppSelector, useAppDispatch } from '@/hooks'
// features
import {
  // auth
  authSelectProfile,
  // post
  PostCard,
  PostCardLocked,
  postSelectRecommendedPosts,
  postSelectRecommendedPostsCount,
  postGetRecommendedPosts,
  postUtilGetPostCardSkeletonSections,
  // subscription
  subscriptionSelectUserSubscriptions,
  // unlock
  unlockOpenUnlockPostModal,
  unlockSelectPostUnlocks,
} from '@/features'
// components
import { ContentLayoutVirtualized, SkeletonVertical } from '@/components'
// styles
import { contentLayout } from '@/styles'

// styles
import { styles } from './styles'

const Row: FC<ListChildComponentProps<any>> = memo(({ index, style }) => {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const profile = useAppSelector(authSelectProfile)
  const postUnlocks = useAppSelector(unlockSelectPostUnlocks)
  const userSubscriptions = useAppSelector(subscriptionSelectUserSubscriptions)
  const posts = useAppSelector(postSelectRecommendedPosts)
  const post = posts[index]

  // if post not fetched
  if (!post) {
    return (
      <div style={style}>
        <SkeletonVertical
          containerWidth="180px"
          sections={postUtilGetPostCardSkeletonSections('post-list-feed-recommended')}
        />
      </div>
    )
  }

  const { id: currentUserId } = profile ?? {}
  const { id: postId, userId: postAuthorUserId, visibility } = post

  const isContentLocked = visibility === EntityPostVisibility.Subscribers
  const isCurrentUserContentAuthor = currentUserId === postAuthorUserId
  const isCurrentUserUnlockedContent = postUnlocks[postId || '']
  const isCurrentUserSubscribedToContentAuthor = userSubscriptions[postAuthorUserId || '']

  const renderLocked =
    isContentLocked &&
    !isCurrentUserContentAuthor &&
    !isCurrentUserUnlockedContent &&
    !isCurrentUserSubscribedToContentAuthor

  // if content is locked
  if (renderLocked) {
    return (
      <div css={styles().posts.main} style={style}>
        <PostCardLocked
          post={post}
          onClick={() => dispatch(unlockOpenUnlockPostModal({ post }))}
          onAuthorNameClick={() => navigate(`${Path.User}/${postAuthorUserId}`)}
          onAuthorUsernameClick={() => navigate(`${Path.User}/${postAuthorUserId}`)}
        />
      </div>
    )
  }

  return (
    <div css={styles().posts.main} style={style}>
      <PostCard
        post={post}
        currentUserId={currentUserId || ''}
        onClick={() => navigate(`${Path.Post}/${postId}`)}
        onAuthorNameClick={() => navigate(`${Path.User}/${postAuthorUserId}`)}
        onAuthorUsernameClick={() => navigate(`${Path.User}/${postAuthorUserId}`)}
      />
    </div>
  )
})

type PostListFeedRecommendedProps = {}

export const PostListFeedRecommended: FC<PostListFeedRecommendedProps> = () => {
  const dispatch = useAppDispatch()
  const recommendedPosts = useAppSelector(postSelectRecommendedPosts)
  const recommendedPostsCount = useAppSelector(postSelectRecommendedPostsCount)

  const postCardWidth = 180
  const postCardMarginRight = 20
  const itemWidth = postCardWidth + postCardMarginRight

  const postCardHeight = 294
  const postCardMarginBottom = 0
  const itemHeight = postCardHeight + postCardMarginBottom

  const defaultContentCount = 1000
  const defaultListWidth = 2238
  const itemsToFetch = 30 // min number of rows to be loaded at a time
  const itemThreshold = 15 // start fetching data when user scrolls within 5 rows

  const isItemLoaded = (index: number) => !!recommendedPosts[index]

  const loadMoreItems = (startIndex: number): void => {
    // (startIndex: number, stopIndex: number): void
    dispatch(postGetRecommendedPosts({ limit: itemsToFetch, offset: startIndex }))
  }

  return (
    <ContentLayoutVirtualized
      title="Posts"
      overrideCss={contentLayout.feed}
      itemWidth={itemWidth}
      itemsCount={recommendedPostsCount}
    >
      <InfiniteLoader
        isItemLoaded={isItemLoaded}
        loadMoreItems={loadMoreItems}
        itemCount={recommendedPostsCount || defaultContentCount}
        minimumBatchSize={itemsToFetch}
        threshold={itemThreshold}
      >
        {({ onItemsRendered, ref }) => (
          <FixedSizeList
            className="virtualized-list"
            height={itemHeight}
            itemSize={itemWidth}
            itemCount={recommendedPostsCount || defaultContentCount}
            onItemsRendered={onItemsRendered}
            ref={ref}
            width={defaultListWidth}
            layout="horizontal"
          >
            {Row}
          </FixedSizeList>
        )}
      </InfiniteLoader>
    </ContentLayoutVirtualized>
  )
}

PostListFeedRecommended.propTypes = {}
